import { htmlToMarkdown, markdownToHtml } from './serialization';

export const DESCRIPTION_STORAGE_LIMIT = 4000;
const BLOCKS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'PRE', 'LI', 'UL', 'OL', 'BLOCKQUOTE']);

export function markdownVisibleText(markdown: string): string {
  const root = document.createElement('div');
  root.innerHTML = markdownToHtml(markdown);
  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
    if (node.nodeName === 'BR') return '\n';
    let result = '';
    let previousBlock = false;
    Array.from(node.childNodes).forEach(child => {
      const block = BLOCKS.has(child.nodeName);
      if (block && (result || previousBlock)) result += '\n';
      result += walk(child);
      previousBlock = block;
    });
    return result;
  };
  return walk(root);
}

export function descriptionLengths(markdown: string) {
  return {
    visible: Array.from(markdownVisibleText(markdown)).length,
    stored: Array.from(markdown).length
  };
}

export function descriptionFits(markdown: string, limit = 2000): boolean {
  const lengths = descriptionLengths(markdown);
  return lengths.visible <= limit && lengths.stored <= DESCRIPTION_STORAGE_LIMIT;
}

export function descriptionChangeAllowed(next: string, previous: string, limit = 2000): boolean {
  const current = descriptionLengths(previous);
  const updated = descriptionLengths(next);
  return (updated.visible <= limit || updated.visible < current.visible) &&
    (updated.stored <= DESCRIPTION_STORAGE_LIMIT || updated.stored < current.stored);
}

export function applyDescriptionInsert(value: string, start: number, end: number, inserted: string) {
  const before = value.slice(0, start);
  const after = value.slice(end);
  const full = before + inserted + after;
  if (descriptionChangeAllowed(full, value)) {
    return { nextValue: full, cursor: start + inserted.length, clipped: false, canInsert: true };
  }

  const characters = Array.from(inserted);
  let low = 1;
  let high = characters.length;
  let fitted = '';
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const part = htmlToMarkdown(markdownToHtml(characters.slice(0, middle).join('')));
    if (part && descriptionChangeAllowed(before + part + after, value)) {
      fitted = part;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return {
    nextValue: fitted ? before + fitted + after : value,
    cursor: fitted ? start + fitted.length : end,
    clipped: true,
    canInsert: !!fitted
  };
}
